import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import { fmtDate, ROLE_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, PencilSimple, Key, MagnifyingGlass } from "@phosphor-icons/react";

const DEPARTMENTS = ["Backend Executives", "OB Sales Executives", "Team Leader", "Tellecaller"];

export default function HR() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/users"); setRows(r.data); }
    catch { toast.error("Failed to load employees"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ login_id: "", password: "", name: "", department: "Backend Executives", designation: "", active: true }); setEditing(null); setOpen(true); };
  const openEdit = (row) => { setForm({ name: row.name, department: row.department || "", designation: row.designation || "", active: row.active !== false }); setEditing(row); setOpen(true); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    try {
      setSaving(true);
      if (editing) {
        await api.put(`/users/${editing.user_id}`, { name: form.name, department: form.department, designation: form.designation, active: form.active });
        toast.success("Employee updated");
      } else {
        if (!form.login_id || !form.password || !form.name) { toast.error("User ID, Password and Name are required"); setSaving(false); return; }
        await api.post("/users", form);
        toast.success("Employee created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const doReset = async () => {
    try {
      const pw = resetFor.newPw || "changeme123";
      const r = await api.post(`/users/${resetFor.user_id}/reset-password`, { new_password: pw });
      toast.success(`Password reset to: ${r.data.new_password}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setResetFor(null); }
  };

  const filtered = rows.filter((r) => !search || `${r.name} ${r.login_id} ${r.department}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="hr-page">
      <PageHeader overline="HR / Documents" title="Employees & Access" subtitle="Create staff accounts with a User ID and password. Department controls their access."
        actions={
          <Button data-testid="employee-add-button" onClick={openCreate} className="rounded-sm bg-brand hover:bg-brand-dark">
            <Plus size={16} className="mr-1.5" weight="bold" /> New Employee
          </Button>
        } />

      <div className="mb-4 relative w-72 max-w-full">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input data-testid="employee-search" placeholder="Search employees…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm border-zinc-200" />
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              {["Name", "User ID", "Department", "Designation", "Access Role", "Status", "Actions"].map((h) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-wider text-zinc-500 font-medium ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-400">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-400">No employees yet.</TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.user_id} data-testid="employee-row" className="border-zinc-100 hover:bg-zinc-50">
                <TableCell className="text-sm font-medium text-zinc-900">{r.name}</TableCell>
                <TableCell className="text-sm text-zinc-600">{r.login_id || r.email}</TableCell>
                <TableCell className="text-sm text-zinc-600">{r.department || "—"}</TableCell>
                <TableCell className="text-sm text-zinc-600">{r.designation || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm border-zinc-300 font-normal">{ROLE_LABEL(r.role)}</Badge></TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm font-normal ${r.active !== false ? "border-emerald-500 text-emerald-600" : "border-zinc-300 text-zinc-400"}`}>{r.active !== false ? "Active" : "Disabled"}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button data-testid="employee-edit-button" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-zinc-900" onClick={() => openEdit(r)}><PencilSimple size={16} /></Button>
                    <Button data-testid="employee-reset-button" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-amber-600" title="Reset password" onClick={() => setResetFor({ ...r, newPw: "" })}><Key size={16} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editing ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {!editing && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-zinc-500 mb-1.5 block">User ID *</Label><Input data-testid="emp-userid" value={form.login_id || ""} onChange={(e) => set("login_id", e.target.value)} className="rounded-sm h-10" placeholder="e.g. rahul.k" /></div>
                <div><Label className="text-xs text-zinc-500 mb-1.5 block">Password *</Label><Input data-testid="emp-password" type="text" value={form.password || ""} onChange={(e) => set("password", e.target.value)} className="rounded-sm h-10" placeholder="min 6 chars" /></div>
              </div>
            )}
            <div><Label className="text-xs text-zinc-500 mb-1.5 block">Employee Name *</Label><Input data-testid="emp-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-sm h-10" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Department</Label>
                <Select value={form.department || ""} onValueChange={(v) => set("department", v)}>
                  <SelectTrigger data-testid="emp-department" className="rounded-sm h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-sm">{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-zinc-500 mb-1.5 block">Designation</Label><Input data-testid="emp-designation" value={form.designation || ""} onChange={(e) => set("designation", e.target.value)} className="rounded-sm h-10" /></div>
            </div>
            {editing && (
              <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-3 h-11">
                <Label className="text-sm text-zinc-600">Active</Label>
                <Switch data-testid="emp-active" checked={!!form.active} onCheckedChange={(v) => set("active", v)} />
              </div>
            )}
            <p className="text-[11px] text-zinc-400">Backend Executives get Disbursement access. OB Sales Executives get Own Brand Sales access. Team Leader &amp; Tellecaller are record-only for now.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="employee-submit-button" onClick={submit} disabled={saving} className="rounded-sm bg-brand hover:bg-brand-dark">{saving ? "Saving…" : editing ? "Save changes" : "Create Employee"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password for {resetFor?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Enter a new password (min 6 chars). Leave blank to use <code>changeme123</code>.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input data-testid="reset-password-input" type="text" placeholder="New password" value={resetFor?.newPw || ""} onChange={(e) => setResetFor((p) => ({ ...p, newPw: e.target.value }))} className="rounded-sm h-10" />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-reset-button" onClick={doReset} className="rounded-sm bg-brand hover:bg-brand-dark">Reset Password</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
